import { View } from "react-native";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "./use-theme";
import IconButton from "../components/IconButton";
import Steps, { StepsConfig } from "../ui_atoms/Steps";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { BUBBLE_ANIMATION } from "../components/Bubble";

type SignupSigninStackTitleProps = {
    currentStepIndex: number;
    goBack?: () => void;
    canGoBack?: () => boolean;
    totalSteps: number;
    onClose?: () => void;
    stepsConfig?: StepsConfig;
    isVisible: boolean;
};

const HEADER_HEIGHT = 64;
const ACTION_WIDTH = 28;

const HeaderComponent = ({
    currentStepIndex,
    goBack,
    canGoBack = () => true,
    totalSteps,
    onClose,
    stepsConfig,
    isVisible,
}: SignupSigninStackTitleProps) => {
    const { marginSize, colors } = useTheme();

    const iconMarginLeft = marginSize - 3;
    const height = useSharedValue(HEADER_HEIGHT);

    const animatedHeaderStyles = useAnimatedStyle(() => ({
        height: height.value,
    }));

    useEffect(() => {
        height.value = withTiming(isVisible ? HEADER_HEIGHT : 0, BUBBLE_ANIMATION);
    }, [height, isVisible]);

    return (
        <Animated.View
            style={[
                animatedHeaderStyles,
                {
                    backgroundColor: colors.background,
                    flexDirection: "row",
                    alignItems: "center",
                },
            ]}
        >
            {canGoBack() ? (
                <IconButton
                    style={{ marginLeft: iconMarginLeft }}
                    onPress={goBack}
                    icon="back"
                    size={ACTION_WIDTH}
                />
            ) : (
                <View style={{ width: ACTION_WIDTH + iconMarginLeft }} />
            )}
            <View style={{ flex: 1 }} />
            {currentStepIndex < totalSteps && (
                <Steps index={currentStepIndex} count={totalSteps} stepsConfig={stepsConfig} />
            )}
            <View style={{ flex: 1 }} />
            {onClose ? (
                <IconButton
                    style={{ marginRight: iconMarginLeft }}
                    onPress={onClose}
                    icon="close"
                    size={ACTION_WIDTH}
                />
            ) : (
                <View style={{ width: ACTION_WIDTH + iconMarginLeft }} />
            )}
        </Animated.View>
    );
};

type UseStepsHeaderProps<T> = {
    getCurrentIndex?: (routeName: T) => number;
    getTotalSteps?: (routeName: T) => number;
    onClose?: () => void;
    stepsConfig?: StepsConfig;
    headerHidden?: boolean[]; // map of indexes where the header is hidden
};

export const useStepsHeader = <T,>({ headerHidden = [], ...params }: UseStepsHeaderProps<T>) => {
    const [goBack, setGoBack] = useState<() => void>();
    const [canGoBack, setCanGoBack] = useState<() => boolean>();
    const [routesNames, setRoutesNames] = useState<T[]>([]);
    const [routeName, setRouteName] = useState<T>();

    const currentIndex = useMemo(
        () =>
            (routeName &&
                (params?.getCurrentIndex?.(routeName) ?? routesNames.indexOf(routeName))) ||
            0,
        [params, routeName, routesNames],
    );

    const totalSteps = useMemo(
        () => (routeName && params?.getTotalSteps?.(routeName)) ?? routesNames.length,
        [params, routeName, routesNames.length],
    );

    const screenListeners = useCallback(
        (newState: {
            navigation?: {
                goBack: () => void;
                canGoBack: () => true;
                getState: () => { routeNames: T[] };
            };
            route: { name: T };
        }) => {
            setGoBack(() => newState.navigation?.goBack);
            setCanGoBack(() => newState.navigation?.canGoBack);
            setRoutesNames(newState.navigation?.getState().routeNames || []);
            setRouteName(newState.route.name);
        },
        [],
    ) as never;

    const headerComponent = useMemo(
        () => (
            <HeaderComponent
                totalSteps={totalSteps}
                currentStepIndex={currentIndex}
                canGoBack={canGoBack}
                goBack={() => {
                    if (canGoBack && !canGoBack()) {
                        params?.onClose?.();
                    } else if (goBack) {
                        goBack();
                    }
                }}
                onClose={params?.onClose}
                stepsConfig={params?.stepsConfig}
                isVisible={!headerHidden[currentIndex]}
            />
        ),
        [totalSteps, currentIndex, goBack, params, headerHidden, canGoBack],
    );

    return { headerComponent, screenListeners };
};
